export const findPendingMessageIndex = (chat, message) => {
  const { echo_id: tempMessageId } = message;
  return chat.messages.findIndex(
    m => m.id === message.id || m.id === tempMessageId
  );
};

export const isConversationIdInRoute = id => {
  return +window?.WOOT?.$route?.params?.conversation_id === id;
};

export const filterByStatus = (chatStatus, filterStatus) =>
  filterStatus === 'all' ? true : chatStatus === filterStatus;

export const filterByInbox = (shouldFilter, inboxId, chatInboxId) => {
  const isOnInbox = Number(inboxId) === chatInboxId;
  return inboxId ? isOnInbox && shouldFilter : shouldFilter;
};

export const filterByTeam = (shouldFilter, teamId, chatTeamId) => {
  const isOnTeam = Number(teamId) === chatTeamId;
  return teamId ? isOnTeam && shouldFilter : shouldFilter;
};

export const filterByLabel = (shouldFilter, labels, chatLabels) => {
  const isOnLabel = labels.every(label => chatLabels.includes(label));
  return labels.length ? isOnLabel && shouldFilter : shouldFilter;
};
export const filterByUnattended = (
  shouldFilter,
  conversationType,
  firstReplyOn,
  waitingSince
) => {
  return conversationType === 'unattended'
    ? (!firstReplyOn || !!waitingSince) && shouldFilter
    : shouldFilter;
};

export const applyPageFilters = (conversation, filters) => {
  if (isConversationIdInRoute(conversation.id)) return true;

  const { inboxId, status, labels = [], teamId, conversationType } = filters;
  const {
    status: chatStatus,
    inbox_id: chatInboxId,
    labels: chatLabels = [],
    meta = {},
    first_reply_created_at: firstReplyOn,
    waiting_since: waitingSince,
  } = conversation;
  const team = meta.team || {};
  const { id: chatTeamId } = team;

  let shouldFilter =
    conversationType === 'archived' || filterByStatus(chatStatus, status);
  shouldFilter = filterByInbox(shouldFilter, inboxId, chatInboxId);
  shouldFilter = filterByTeam(shouldFilter, teamId, chatTeamId);
  shouldFilter = filterByLabel(shouldFilter, labels, chatLabels);
  shouldFilter = filterByUnattended(
    shouldFilter,
    conversationType,
    firstReplyOn,
    waitingSince
  );

  return shouldFilter;
};

const filterValue = (v, k, inNum = false) => {
  let value = v && typeof v === 'object' && k in v ? v[k] : v;
  return inNum ? Number(value) : value;
};

export const getCombinedFilters = (
  customViews,
  appliedFilters,
  conversationListConditions
) => {
  let filters = appliedFilters;
  if (conversationListConditions?.type === 'custom_view') {
    const customViewId = +conversationListConditions.customViewId;
    const customView = customViews.find(item => +item.id === customViewId);
    filters = Array.isArray(customView?.query?.payload)
      ? customView.query.payload
      : [];
  }
  return filters;
};

export const checkConversationConditionMatch = (
  conversation,
  filters,
  conditions,
  chatStatusFilter = 'open'
) => {
  if (
    !filters.length &&
    chatStatusFilter &&
    chatStatusFilter !== 'all' &&
    chatStatusFilter !== conversation.status
  ) {
    return false;
  }

  let conditionsMatch = [];
  let conditionMatch = true;

  if (conditions?.type) {
    if (conditions.type === 'mention') {
      conditionMatch =
        conversation.mentioned_agent_ids.length &&
        conversation.mentioned_agent_ids.includes(conditions?.userId);
    } else if (conditions.type === 'unattended') {
      conditionMatch = !conversation.first_reply_created_at;
    } else if (conditions.type === 'archived') {
      conditionMatch = conversation.archived;
    } else if (conditions.type === 'team') {
      conditionMatch = checkConversationTeamConditionMatch(
        conversation.meta.team?.id,
        'equal_to',
        { id: +conditions?.teamId },
        conditionMatch
      );
    } else if (conditions.type === 'inbox') {
      conditionMatch = checkConversationChannelsConditionMatch(
        conversation.inbox_id,
        'equal_to',
        [conditions?.inboxId],
        conditionMatch
      );
    } else if (conditions.type === 'label') {
      conditionMatch = checkConversationLabelsConditionMatch(
        conversation.labels,
        'equal_to',
        [conditions?.label],
        conditionMatch
      );
    }
  }

  if (!conditionMatch) return false;

  if (filters.length) {
    filters.forEach((filter, idx, arr) => {
      if (!filter.attribute_key) return;

      if (filter.attribute_key === 'labels') {
        conditionMatch = checkConversationLabelsConditionMatch(
          conversation.labels,
          filter.filter_operator,
          filter.values,
          conditionMatch
        );
      } else if (filter.attribute_key === 'team_id') {
        conditionMatch = checkConversationTeamConditionMatch(
          conversation.meta.team?.id,
          filter.filter_operator,
          filter.values,
          conditionMatch
        );
      } else if (filter.attribute_key === 'assignee_id') {
        conditionMatch = checkConversationAssigneeConditionMatch(
          conversation.meta.assignee?.id,
          filter.filter_operator,
          filter.values,
          conditionMatch
        );
      } else if (filter.attribute_key === 'inbox_id') {
        conditionMatch = checkConversationChannelsConditionMatch(
          conversation.inbox_id,
          filter.filter_operator,
          filter.values,
          conditionMatch
        );
      } else if (filter.attribute_key === 'display_id') {
        conditionMatch = checkConversationIdentifierConditionMatch(
          conversation.id,
          filter.filter_operator,
          filter.values,
          conditionMatch
        );
      } else if (filter.attribute_key === 'created_at') {
        conditionMatch = checkConversationCreatedAtConditionMatch(
          conversation.created_at,
          filter.filter_operator,
          filter.values,
          conditionMatch
        );
      } else if (filter.attribute_key === 'last_activity_at') {
        conditionMatch = checkConversationActivityAtConditionMatch(
          conversation.timestamp,
          filter.filter_operator,
          filter.values,
          conditionMatch
        );
      } else if (filter.attribute_key === 'browser_language') {
        conditionMatch = checkConversationLanguageConditionMatch(
          conversation.additional_attributes?.browser_language,
          filter.filter_operator,
          filter.values,
          conditionMatch
        );
      } else if (filter.attribute_key === 'country_code') {
        conditionMatch = checkConversationCountryConditionMatch(
          conversation.additional_attributes?.country_code,
          filter.filter_operator,
          filter.values,
          conditionMatch
        );
      } else if (filter.attribute_key === 'referer') {
        conditionMatch = checkConversationRefererConditionMatch(
          conversation.additional_attributes?.referer,
          filter.filter_operator,
          filter.values,
          conditionMatch
        );
      } else if (filter.attribute_key === 'status') {
        conditionMatch = checkConversationStatusConditionMatch(
          conversation.status,
          filter.filter_operator,
          filter.values,
          conditionMatch
        );
      } else if (filter.attribute_key === 'campaign_id') {
        conditionMatch = true;
      } else if (filter.attribute_key === 'rating') {
        conditionMatch = true;
      } else {
        conditionMatch = checkConversationCustomAttributesConditionMatch(
          conversation.custom_attributes?.[filter.attribute_key],
          filter.filter_operator,
          filter.values,
          conditionMatch
        );
      }

      if (filter.query_operator === 'or' || idx === arr.length - 1) {
        conditionsMatch.push(conditionMatch);
        conditionMatch = true;
      }
    });
    return conditionsMatch.includes(true);
  }

  return conditionMatch;
};

function checkConversationStatusConditionMatch(
  status = null,
  operator,
  values = [],
  conditionMatch
) {
  switch (operator) {
    case 'equal_to': {
      const statuses = Array.isArray(values)
        ? values.map(v => filterValue(v, 'id'))
        : [];
      return (
        conditionMatch &&
        (statuses.includes(status) || statuses.includes('all'))
      );
    }
    case 'not_equal_to': {
      const statuses = Array.isArray(values)
        ? values.map(v => filterValue(v, 'id'))
        : [];
      return conditionMatch && !statuses.includes(status);
    }
    default:
      return true;
  }
}

function checkConversationChannelsConditionMatch(
  inboxId = null,
  operator,
  values = [],
  conditionMatch
) {
  switch (operator) {
    case 'is_present':
      return conditionMatch && !!inboxId;
    case 'is_not_present':
      return conditionMatch && !inboxId;
    case 'equal_to': {
      const ids = Array.isArray(values)
        ? values.map(v => filterValue(v, 'id', true))
        : [];
      return conditionMatch && ids.includes(inboxId);
    }
    case 'not_equal_to': {
      const ids = Array.isArray(values)
        ? values.map(v => filterValue(v, 'id', true))
        : [];
      return conditionMatch && !ids.includes(inboxId);
    }
    default:
      return true;
  }
}

function checkConversationIdentifierConditionMatch(
  id = null,
  operator,
  values = null,
  conditionMatch
) {
  const value = Array.isArray(values) ? values[0] : values;
  switch (operator) {
    case 'contains':
      return conditionMatch && String(id)?.includes(value);
    case 'does_not_contain':
      return conditionMatch && !String(id)?.includes(value);
    case 'equal_to':
      return id === value;
    case 'not_equal_to':
      return id !== value;
    default:
      return true;
  }
}

function checkConversationLabelsConditionMatch(
  labels = [],
  operator,
  values = [],
  conditionMatch
) {
  const hasLabels = labels.length > 0;
  switch (operator) {
    case 'is_present':
      return conditionMatch && hasLabels;
    case 'is_not_present':
      return conditionMatch && !hasLabels;
    case 'equal_to': {
      const names = Array.isArray(values)
        ? values.map(v => filterValue(v, 'name'))
        : [];
      const labelSet = new Set(labels);
      return conditionMatch && names.some(name => labelSet.has(name));
    }
    case 'not_equal_to': {
      const names = Array.isArray(values)
        ? values.map(v => filterValue(v, 'name'))
        : [];
      const labelSet = new Set(labels);
      return conditionMatch && names.every(name => !labelSet.has(name));
    }
    default:
      return true;
  }
}

function checkConversationTeamConditionMatch(
  teamId = null,
  operator,
  values = {},
  conditionMatch
) {
  const value = Array.isArray(values) ? values[0] : values?.id;
  switch (operator) {
    case 'is_present':
      return conditionMatch && !!teamId;
    case 'is_not_present':
      return conditionMatch && !teamId;
    case 'equal_to':
      return conditionMatch && teamId === value;
    case 'not_equal_to':
      return conditionMatch && teamId !== value;
    default:
      return true;
  }
}

function checkConversationAssigneeConditionMatch(
  assigneeId = null,
  operator,
  values = {},
  conditionMatch
) {
  const value = Array.isArray(values) ? values[0] : values?.id;
  switch (operator) {
    case 'is_present':
      return conditionMatch && !!assigneeId;
    case 'is_not_present':
      return conditionMatch && !assigneeId;
    case 'equal_to':
      return conditionMatch && assigneeId === value;
    case 'not_equal_to':
      return conditionMatch && assigneeId !== value;
    default:
      return true;
  }
}

function checkConversationCreatedAtConditionMatch(
  createdAt = null,
  operator,
  values = null,
  conditionMatch
) {
  const value = Array.isArray(values) ? values[0] : values;
  switch (operator) {
    case 'is_greater_than':
      return conditionMatch && createdAt > new Date(value).getTime() / 1000;
    case 'is_less_than':
      return conditionMatch && createdAt < new Date(value).getTime() / 1000;
    case 'days_before': {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const targetDate = new Date(today);
      targetDate.setDate(today.getDate() - value);
      const targetTimestamp = Math.floor(targetDate.getTime() / 1000);
      const isExactXDaysBefore =
        createdAt >= targetTimestamp && createdAt < targetTimestamp + 86400;
      return conditionMatch && isExactXDaysBefore;
    }
    case 'between': {
      return conditionMatch && createdAt >= values[0] && createdAt <= values[1];
    }
    default:
      return true;
  }
}

function checkConversationActivityAtConditionMatch(
  activityAtTimestamp = null,
  operator,
  values = null,
  conditionMatch
) {
  const value = Array.isArray(values) ? values[0] : values;
  switch (operator) {
    case 'is_greater_than':
      return (
        conditionMatch && activityAtTimestamp > new Date(value).getTime() / 1000
      );
    case 'is_less_than':
      return (
        conditionMatch && activityAtTimestamp < new Date(value).getTime() / 1000
      );
    case 'days_before': {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const targetDate = new Date(today);
      targetDate.setDate(today.getDate() - value);
      const targetTimestamp = Math.floor(targetDate.getTime() / 1000);
      const isExactXDaysBefore =
        activityAtTimestamp >= targetTimestamp &&
        activityAtTimestamp < targetTimestamp + 86400;
      return conditionMatch && isExactXDaysBefore;
    }
    case 'between': {
      return (
        conditionMatch &&
        activityAtTimestamp >= values[0] &&
        activityAtTimestamp <= values[1]
      );
    }
    default:
      return true;
  }
}

function checkConversationLanguageConditionMatch(
  language = null,
  operator,
  values = {},
  conditionMatch
) {
  const value = Array.isArray(values) ? values[0] : values?.id;
  switch (operator) {
    case 'equal_to':
      return conditionMatch && language === value;
    case 'not_equal_to':
      return conditionMatch && language !== value;
    default:
      return true;
  }
}

function checkConversationCountryConditionMatch(
  country = null,
  operator,
  values = {},
  conditionMatch
) {
  const value = Array.isArray(values) ? values[0] : values?.id;
  switch (operator) {
    case 'equal_to':
      return conditionMatch && country?.toLowerCase() === value?.toLowerCase();
    case 'not_equal_to':
      return conditionMatch && country?.toLowerCase() !== value?.toLowerCase();
    default:
      return true;
  }
}

function checkConversationRefererConditionMatch(
  refer = null,
  operator,
  values = null,
  conditionMatch
) {
  const value = Array.isArray(values) ? values[0] : values;
  switch (operator) {
    case 'contains':
      return conditionMatch && refer?.includes(value);
    case 'does_not_contain':
      return conditionMatch && !refer?.includes(value);
    case 'equal_to':
      return refer === value;
    case 'not_equal_to':
      return refer !== value;
    default:
      return true;
  }
}

function checkConversationCustomAttributesConditionMatch(
  attribute = null,
  operator,
  values = null,
  conditionMatch
) {
  const value = Array.isArray(values) ? values[0] : values;
  switch (operator) {
    case 'contains':
      return conditionMatch && attribute?.includes(value);
    case 'does_not_contain':
      return conditionMatch && !attribute?.includes(value);
    case 'equal_to':
      return attribute === value;
    case 'not_equal_to':
      return attribute !== value;
    default:
      return true;
  }
}
